// components/journey-surface/journey-lenses.js
// ═══════════════════════════════════════════════
// Lens definitions and LensToggle button for the journey map.
// Each lens stacks a visual modifier on top of the base tree.
// See docs/JOURNEY_MAP_INTELLIGENCE.md for the design rationale.

import React, { useState } from 'react';

// ── Lens definitions ───────────────────────────────────────
// The journey map renders one tree by default. Lenses stack visual
// modifiers on top of it. Each lens has its own intelligence workstream
// that lands as its own commit; until then, toggling a lens marks the
// architecture as "armed but unbuilt". See docs/JOURNEY_MAP_INTELLIGENCE.md.
export const LENSES = [
  {
    id: 'gaps',
    label: 'Gaps',
    description: 'Where your knowledge is thin — sage where reinforced, gray where touched once, faint where bridges are missing. The gap heatmap.',
    icon: (color) => (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="8" r="6" stroke={color} strokeWidth="1.3" />
        <path d="M2 8a6 6 0 0 1 12 0" fill={color} fillOpacity="0.25" />
      </svg>
    ),
  },
  {
    id: 'connections',
    label: 'Connections',
    description: 'Cross-branch links between conceptually related nodes',
    icon: (color) => (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <circle cx="3.5" cy="4" r="1.6" stroke={color} strokeWidth="1.2" />
        <circle cx="12.5" cy="4" r="1.6" stroke={color} strokeWidth="1.2" />
        <circle cx="8" cy="12" r="1.6" stroke={color} strokeWidth="1.2" />
        <path d="M3.5 4 L8 12 L12.5 4" stroke={color} strokeWidth="1" strokeDasharray="1.5 1.5" />
      </svg>
    ),
  },
  {
    id: 'ghosts',
    label: 'Ghosts',
    description: 'Predicted nodes that don\u2019t exist yet',
    icon: (color) => (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="8" r="5.5" stroke={color} strokeWidth="1.2" strokeDasharray="2 2" />
        <circle cx="8" cy="8" r="1.4" fill={color} fillOpacity="0.4" />
      </svg>
    ),
  },
  {
    id: 'health',
    label: 'Health',
    description: 'Fork age, momentum, abandoned-fork dimming',
    icon: (color) => (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <path d="M2 9 L5 9 L6.5 5 L9.5 13 L11 9 L14 9" stroke={color} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
];

// ── Lens toggle button ─────────────────────────────────────
export function LensToggle({ lens, active, onClick, onHover }) {
  const [hovered, setHovered] = useState(false);
  const color = active ? '#c8a96e' : hovered ? '#7a9e82' : '#666';
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => { setHovered(true); onHover?.(lens); }}
      onMouseLeave={() => { setHovered(false); onHover?.(null); }}
      style={{
        background: active ? 'rgba(200,169,110,0.1)' : 'none',
        border: active ? '1px solid rgba(200,169,110,0.4)' : '1px solid transparent',
        borderRadius: 4,
        padding: 4,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.12s',
      }}
    >
      {lens.icon(color)}
    </button>
  );
}
